import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const values = [
  {
    title: "Excellence",
    description: "We never settle for mediocre. Every detail matters, every pixel counts."
  },
  {
    title: "Integrity",
    description: "Honest communication and transparent processes build lasting relationships."
  },
  {
    title: "Innovation",
    description: "We stay ahead of trends while creating timeless design solutions."
  },
  {
    title: "Partnership",
    description: "Your success is our success. We're invested in your growth."
  }
];

export default function About() {
  return (
    <div className="bg-[#050505] min-h-screen pt-24 md:pt-32" data-testid="about-page">
      {/* Header */}
      <section className="section-padding pb-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
              About Us
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white mb-6">
              The Art of <span className="italic">Digital</span> Craft
            </h1>
            <p className="text-[#A1A1A1] text-base md:text-lg leading-relaxed">
              Aakaar Designs is a premium design studio dedicated to creating 
              exceptional digital experiences that elevate brands and drive results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.8 }}
        className="px-6 md:px-12 lg:px-16"
      >
        <div className="max-w-7xl mx-auto">
          <div className="aspect-[21/9] overflow-hidden bg-[#0F0F0F]">
            <img
              src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1400&q=80"
              alt="Aakaar Designs Studio"
              className="w-full h-full object-cover opacity-90"
              data-testid="about-hero-image"
            />
          </div>
        </div>
      </motion.div>

      {/* Story Section */}
      <section className="section-padding" data-testid="story-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
                Our Story
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
                Crafting digital <span className="italic">excellence</span>
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="space-y-6"
            >
              <p className="text-[#A1A1A1] text-base leading-relaxed">
                Aakaar Designs was founded with a singular vision: to bring premium 
                design quality to brands that understand the power of exceptional 
                digital presence. We believe that great design is not a luxury—it's 
                a strategic investment.
              </p>
              <p className="text-[#A1A1A1] text-base leading-relaxed">
                Our approach combines aesthetic excellence with strategic thinking. 
                Every project we undertake is an opportunity to create something 
                meaningful, something that resonates with your audience and achieves 
                your business objectives.
              </p>
              <p className="text-[#A1A1A1] text-base leading-relaxed">
                We work with founders, startups, and established brands who share 
                our commitment to quality and understand that exceptional design 
                creates exceptional results.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Quote */}
      <section className="section-padding border-t border-b border-white/5" data-testid="philosophy-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <p className="font-accent text-3xl md:text-4xl lg:text-5xl italic text-white/90 leading-relaxed">
              "Design is not just what it looks like and feels like. 
              Design is <span className="text-[#C5A059]">how it works</span>."
            </p>
            <p className="text-[#A1A1A1] mt-8 uppercase tracking-widest text-sm">
              — Our Philosophy
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding" data-testid="values-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
              Our Values
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white">
              What We <span className="italic">Stand For</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="text-center"
                data-testid={`value-${value.title.toLowerCase()}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 border border-[#C5A059]/30 flex items-center justify-center">
                  <span className="font-heading text-2xl text-[#C5A059]">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="font-heading text-xl text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-[#A1A1A1] text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="section-padding border-t border-white/5">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative order-2 lg:order-1"
            >
              <div className="aspect-[4/5] bg-[#0F0F0F] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80"
                  alt="Our approach"
                  className="w-full h-full object-cover opacity-80"
                />
              </div>
              <div className="absolute -top-4 -right-4 w-32 h-32 border border-[#C5A059]/30" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-2"
            >
              <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
                Our Approach
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
                Detail-obsessed <span className="italic">design</span>
              </h2>
              <div className="space-y-6">
                <p className="text-[#A1A1A1] text-base leading-relaxed">
                  We believe that the difference between good and exceptional lies 
                  in the details. Every curve, every color choice, every interaction 
                  is carefully considered and purposefully executed.
                </p>
                <p className="text-[#A1A1A1] text-base leading-relaxed">
                  Our process is collaborative and transparent. We work closely with 
                  our clients to understand their vision, challenge assumptions, and 
                  create solutions that exceed expectations.
                </p>
                <p className="text-[#A1A1A1] text-base leading-relaxed">
                  The result? Digital experiences that not only look stunning but 
                  also perform exceptionally well, driving real business results.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding border-t border-white/5">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#A1A1A1] uppercase tracking-[0.3em] text-xs mb-6">
              Let's work together
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-8">
              Ready to elevate your <span className="italic text-[#C5A059]">brand</span>?
            </h2>
            <a
              href="https://wa.me/918006521008"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-3"
              data-testid="about-whatsapp-cta"
            >
              Start a Conversation
              <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
